import React from 'react';
import { Flex, HStack, VStack, Box, InlineBanner, Text } from '@cad-arc';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { EventPanelFormBody } from './event-panel-form/event-panel-form-body';
import { useEventMetadataStore } from './event-panel-form/event-metadata';
import { useExpandablePanelSize } from '../panels/expandable-panel-size';
import { EventPanelNarrative } from './event-panel-narrative';
import { FormattedDurationSince } from '../common/components/formatted-duration-since';
import { EventPanelTabs } from './tabs/components/event-panel-tabs';
import { UnitsDisplay } from '../common/components/units-display';
import { useEventPanelUnits } from './units/use-event-panel-units';
import { formatMonthDayYearHoursMinutes } from '../common/helpers/utils';
import { observer } from 'mobx-react-lite';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { useFormState } from 'react-hook-form';
import { ErrorBoundary } from '../monitoring/error-boundary';
import { useEventPanelBanners } from './event-panel-banners';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { EventStaffCountDisplay } from './event-staff-count-display';
import { EventPanelFormType } from '@mark43/cad-api';
import { useWorkspacePanel } from '../panels/hooks/use-workspace-panel';
import { NARRATIVE_PANEL_WITH_TWO_INFOS_HEIGHT } from '../panels/entity-panels/entity-panel-components';

const ScheduledEventDetails = observer(
    (): React.ReactElement => {
        const eventMetadataStore = useEventMetadataStore();
        const { scheduledEventScheduledDateUtc, scheduledEventFrequencyType } =
            eventMetadataStore.state.metadata ?? {};
        return (
            <Flex w="100%" justifyContent="flex-end">
                <VStack spacing={0} alignItems="flex-end">
                    <Text variant="caption" color="info">
                        SCHEDULED
                    </Text>
                    {!!scheduledEventScheduledDateUtc && (
                        <Text variant="caption" color="info">
                            {formatMonthDayYearHoursMinutes(scheduledEventScheduledDateUtc)}
                        </Text>
                    )}
                    {!!scheduledEventFrequencyType && scheduledEventFrequencyType !== 'ONCE' && (
                        <Text variant="caption" color="info">
                            RECURRING {scheduledEventFrequencyType}
                        </Text>
                    )}
                </VStack>
            </Flex>
        );
    }
);

const UnitsAndStandingMinutes = observer(
    (): React.ReactElement => {
        const eventMetadataStore = useEventMetadataStore();
        const eventPanelUnits = useEventPanelUnits();
        const { timeTransmitted } = eventMetadataStore.state.metadata ?? {};
        const { subpremiseSupport } = useEnabledFeatures();

        return (
            <>
                <EventStaffCountDisplay eventHasUnits={eventPanelUnits.length > 0} />
                <HStack
                    w="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ marginTop: 0 }}
                >
                    {eventPanelUnits ? (
                        <UnitsDisplay
                            units={eventPanelUnits}
                            minNumberToDisplay={5}
                            enableContextMenu={true}
                        />
                    ) : undefined}
                    {timeTransmitted && eventMetadataStore.state?.metadata?.isActive ? (
                        <Text fontSize="sm" color="brand">
                            SM:
                            <FormattedDurationSince
                                date={new Date(timeTransmitted)}
                                color="brand"
                            />
                        </Text>
                    ) : undefined}
                </HStack>
                {subpremiseSupport && (
                    <Box alignSelf="start">
                        <Text variant="headingSm">Event Info</Text>
                    </Box>
                )}
            </>
        );
    }
);

export const EventPanelLeftPane = observer(function EventPanelLeftPane(): React.ReactElement {
    const eventPanelSize = useExpandablePanelSize();
    const eventMetadataStore = useEventMetadataStore();
    const integratedForm = useIntegratedForm();
    const eventPanelBanners = useEventPanelBanners();
    const isReadOnly = integratedForm.mode === 'read-only' ? true : false;
    const { agencyEventNumber, isScheduledEvent } = eventMetadataStore.state.metadata ?? {};
    const isSavedEvent = agencyEventNumber !== undefined;
    const eventForm = useIntegratedForm<EventPanelFormType>().form;
    const { isValid, isSubmitted } = useFormState({
        control: eventForm.control,
    });
    const workspacePanel = useWorkspacePanel<'EVENT'>();
    const shouldFocusEventPanelForm =
        workspacePanel.getPanelState('shouldFocusEventPanelForm') ?? true;

    return (
        <VStack spacing={0} height="100%" w="500px" overflowX="hidden">
            <VStack
                w="100%"
                h="100%"
                overflowY="auto"
                flexGrow={1}
                position="relative"
                spacing={0}
                backgroundColor={isReadOnly ? 'var(--arc-colors-surface-background)' : undefined}
            >
                <VStack w="100%" p="var(--arc-space-4)" flex="1">
                    {workspacePanel.isAlerting && (
                        <InlineBanner
                            style={{ width: '100%' }}
                            status="attention"
                            description="Unsaved Event Changes"
                        />
                    )}
                    {!isValid && isSubmitted ? (
                        <InlineBanner
                            style={{ width: '100%' }}
                            status="error"
                            description="One or more issues need to be fixed"
                        />
                    ) : undefined}
                    {eventMetadataStore?.state.metadata?.isAgencyExternal ? (
                        <InlineBanner
                            style={{ width: '100%' }}
                            status="info"
                            description="Any changes made to this event after requesting for assistance will only be saved internally and will not be sent to external agencies."
                        />
                    ) : undefined}

                    {eventPanelBanners?.map((banner, idx) => (
                        <InlineBanner key={idx} style={{ width: '100%' }} {...banner} />
                    ))}
                    {isScheduledEvent ? <ScheduledEventDetails /> : <UnitsAndStandingMinutes />}
                    <FormBody
                        formBodyStyle={{ width: '100%' }}
                        focusForm={shouldFocusEventPanelForm}
                    >
                        <EventPanelFormBody />
                    </FormBody>
                </VStack>
                {eventPanelSize === 'NORMAL' && isSavedEvent && (
                    // TODO: Fix this layout when we aren't dependent on sharing the narrative with legacy CAD.
                    <Box
                        w="100%"
                        flexGrow={1}
                        h="100%"
                        overflowY="auto"
                        minH={NARRATIVE_PANEL_WITH_TWO_INFOS_HEIGHT}
                    >
                        <ErrorBoundary name="EVENT_PANEL_ADDITIONAL_INFOS">
                            <EventPanelNarrative />
                        </ErrorBoundary>
                    </Box>
                )}
                <Box flexGrow={1000}>
                    {/* take up space so EventPanelTabs sits at the bottom when panel is in XL mode and narrative is in right hand side*/}
                </Box>
            </VStack>
            {(isSavedEvent && eventPanelSize !== 'XXL') || eventPanelSize === 'NORMAL' ? (
                <EventPanelTabs mode="INLINE" />
            ) : undefined}
        </VStack>
    );
});
